/**
 * V3 New Conversation Modal — Patient-side
 * Requires: clinic selector → case selector → subject → message
 * Only active when CASE_MESSAGING_V3_ENABLED=true.
 * When V3 is OFF, falls back to the legacy NewThreadForm.
 *
 * HARDENING: If patient has no existing cases/clinics, shows a clear empty
 * state with redirect to Find Clinics / Request Quote. Does NOT auto-create cases.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Building2, Briefcase, Send, X, ChevronRight, Loader2, Search
} from 'lucide-react';
import { isV3Enabled } from '../utils/featureFlags';
import CaseMessagingAPI from '../services/CaseMessagingAPI';
import MessagingAPI from '../services/MessagingAPI';
import api from '../utils/api';
import { toast } from 'sonner';

const NewConversationV3Modal = ({
  isOpen,
  onClose,
  onThreadCreated,
  userId,
  userRole = 'patient',
  prefilledClinicId = null,
  prefilledCaseId = null,
  prefilledSubject = '',
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(prefilledClinicId ? (prefilledCaseId ? 3 : 2) : 1);
  const [clinics, setClinics] = useState([]);
  const [cases, setCases] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(prefilledClinicId || '');
  const [selectedCase, setSelectedCase] = useState(prefilledCaseId || '');
  const [subject, setSubject] = useState(prefilledSubject);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [clinicSearch, setClinicSearch] = useState('');
  const [caseSearch, setCaseSearch] = useState('');

  // Load patient cases when modal opens
  useEffect(() => {
    if (!isOpen || !isV3Enabled()) return;
    loadPatientCases();
  }, [isOpen, userId]);

  const loadPatientCases = useCallback(async () => {
    try {
      setLoading(true);
      // ── B-2 / M2 — canonical candidate authority ──────────────────
      // Owner Audit §3 M2 (PA-08): the modal used to derive clinics
      // ONLY from `treatment_cases`, showing a false empty state to
      // patients whose relationship with a clinic sits on `leads` or
      // `message_threads` (pre-acceptance).  We now consume the
      // canonical UNION endpoint (see /api/patient/conversation-
      // candidates). Cases are still returned so the case-selector
      // step keeps working when a case exists.
      const res = await api.get('/patient/conversation-candidates');
      const payload = res?.data || {};
      const patientCases = payload.cases || [];
      setCases(patientCases);

      // If the canonical endpoint returned unioned clinic candidates,
      // prefer that list verbatim (already deduped + name-resolved).
      const unionedClinics = Array.isArray(payload.clinics) ? payload.clinics : [];
      if (unionedClinics.length) {
        setClinics(unionedClinics.map(c => ({
          id: c.id,
          name: c.name || 'Provider',
        })));
      } else {
        setClinics([]);
      }
    } catch (err) {
      console.error('Failed to load conversation candidates:', err);
      // Last-resort fallback so a transient candidates-endpoint blip
      // never leaves the patient with a broken modal — read cases
      // directly. This preserves the pre-B-2 behaviour on failure.
      try {
        const response = await CaseMessagingAPI.getPatientCases(userId);
        const patientCases = response.cases || [];
        setCases(patientCases);
        const clinicMap = {};
        patientCases.forEach(c => {
          if (c.clinic_id && !clinicMap[c.clinic_id]) {
            clinicMap[c.clinic_id] = {
              id: c.clinic_id,
              name: c.clinic_name || 'Provider',
            };
          }
        });
        setClinics(Object.values(clinicMap));
      } catch (e2) {
        setCases([]);
        setClinics([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const filteredClinics = clinics.filter(c =>
    !clinicSearch || c.name.toLowerCase().includes(clinicSearch.toLowerCase()) || c.id.includes(clinicSearch)
  );

  const filteredCases = cases
    .filter(c => c.clinic_id === selectedClinic)
    .filter(c => !caseSearch || (c.treatment_name || '').toLowerCase().includes(caseSearch.toLowerCase()));

  const handleSelectClinic = (clinicId) => {
    setSelectedClinic(clinicId);
    setSelectedCase('');
    // ── B-2 / M2 — auto-skip case picker when there is no case yet.
    // Pre-acceptance conversations (lead / quote / provider-invite)
    // legitimately have NO `treatment_cases` row (Batch-1.2.1 retired
    // the fake-case bootstrap). Backend V3 validator explicitly
    // permits `case_id=None` for pre-acceptance contexts, so we jump
    // straight to the compose step instead of showing an empty case
    // list.
    const clinicHasCases = cases.some(c => c.clinic_id === clinicId);
    setStep(clinicHasCases ? 2 : 3);
  };

  const handleSelectCase = (caseId) => {
    setSelectedCase(caseId);
    setStep(3);
  };

  const handleCreate = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error(t('provider.messaging.newConversation.toastMissingFields'));
      return;
    }
    try {
      setCreating(true);
      // ── B-2 / M2 — send `case_id` only when the patient actually
      // has one on this clinic; otherwise let the backend V3
      // validator treat this as a pre-acceptance (lead/offer/null)
      // context. Prevents "V3: case_id is required" for legitimate
      // pre-acceptance conversations.
      const response = await CaseMessagingAPI.createThreadV3({
        threadType: 'clinic',
        clinicId: selectedClinic,
        caseId: selectedCase || undefined,
        subject,
        initialMessage: message,
      });
      if (response.thread) {
        toast.success(t('provider.messaging.newConversation.toastStarted'));
        onThreadCreated?.(response.thread);
        handleClose();
      }
    } catch (err) {
      console.error('Failed to create thread:', err);
      const msg = err?.response?.data?.detail || t('provider.messaging.newConversation.toastFailed');
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedClinic('');
    setSelectedCase('');
    setSubject('');
    setMessage('');
    setClinicSearch('');
    setCaseSearch('');
    onClose();
  };

  const handleGoToFindClinics = () => {
    handleClose();
    // Navigate to treatments/clinics page for quote requests
    window.location.hash = '#/treatments';
  };

  if (!isOpen) return null;

  // If V3 is NOT enabled, don't render this modal (caller should use legacy)
  if (!isV3Enabled()) return null;

  const selectedCaseObj = cases.find(c => c.case_id === selectedCase);
  const selectedClinicObj = clinics.find(c => c.id === selectedClinic);

  // HARDENING: Determine if patient has no existing relationships
  const hasNoClinics = !loading && clinics.length === 0;
  const hasNoCasesForClinic = !loading && step === 2 && filteredCases.length === 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="v3-new-conversation-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="p-1 hover:bg-slate-100 rounded-full transition-colors"
                data-testid="v3-modal-back"
              >
                <ChevronRight className="h-4 w-4 rotate-180 text-slate-500" />
              </button>
            )}
            <h3 className="font-semibold text-secondary" data-testid="v3-modal-title">
              {step === 1 && t('provider.messaging.newConversation.stepSelectClinic')}
              {step === 2 && t('provider.messaging.newConversation.stepSelectCase')}
              {step === 3 && t('provider.messaging.newConversation.stepNewConversation')}
            </h3>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-slate-100 rounded-full" data-testid="v3-modal-close">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Breadcrumb */}
        {(selectedClinic || selectedCase) && (
          <div className="px-5 py-2 bg-slate-50 border-b flex items-center gap-2 text-xs text-slate-500" data-testid="v3-breadcrumb">
            {selectedClinicObj && (
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {selectedClinicObj.name}
              </span>
            )}
            {selectedCaseObj && (
              <>
                <ChevronRight className="h-3 w-3" />
                <span className="flex items-center gap-1">
                  <Briefcase className="h-3 w-3" />
                  {selectedCaseObj.treatment_name || selectedCaseObj.case_id.slice(0, 8)}
                </span>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-slate-500">{t('provider.messaging.newConversation.loadingCases')}</p>
            </div>
          ) : (
            <>
              {/* Step 1: Clinic Selector */}
              {step === 1 && (
                <div className="space-y-3" data-testid="v3-clinic-selector">
                  {hasNoClinics ? (
                    /* HARDENING: Empty state — no existing clinic relationships */
                    <div className="text-center py-8" data-testid="v3-no-clinics-empty-state">
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                        <Building2 className="h-8 w-8 text-slate-400" />
                      </div>
                      <h4 className="font-medium text-secondary mb-2">{t('provider.messaging.newConversation.noClinicsTitle')}</h4>
                      <p className="text-sm text-slate-500 mb-4 max-w-xs mx-auto">
                        {t('provider.messaging.newConversation.noClinicsBodyPrefix')}
                        <strong>{t('provider.messaging.newConversation.noClinicsBodyCta')}</strong>
                        {t('provider.messaging.newConversation.noClinicsBodySuffix')}
                      </p>
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700 max-w-xs mx-auto">
                        <strong>{t('provider.messaging.newConversation.howItWorksLabel')}</strong> {t('provider.messaging.newConversation.howItWorksBody')}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          value={clinicSearch}
                          onChange={e => setClinicSearch(e.target.value)}
                          placeholder={t('provider.messaging.newConversation.clinicSearchPlaceholder')}
                          className="pl-9"
                          data-testid="v3-clinic-search"
                        />
                      </div>
                      {filteredClinics.length === 0 ? (
                        <div className="text-center py-6 text-slate-500 text-sm">
                          <p>{t('provider.messaging.newConversation.clinicNoMatch')}</p>
                        </div>
                      ) : (
                        filteredClinics.map(clinic => (
                          <button
                            key={clinic.id}
                            onClick={() => handleSelectClinic(clinic.id)}
                            className="w-full flex items-center gap-3 p-3 rounded-lg border hover:border-primary hover:bg-primary/5 transition-all text-left"
                            data-testid={`v3-clinic-option-${clinic.id}`}
                          >
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{clinic.name}</p>
                              <p className="text-xs text-slate-400 truncate">{clinic.id}</p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-slate-300" />
                          </button>
                        ))
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Case Selector */}
              {step === 2 && (
                <div className="space-y-3" data-testid="v3-case-selector">
                  {hasNoCasesForClinic ? (
                    /* HARDENING: Empty state — no cases for this clinic */
                    <div className="text-center py-8" data-testid="v3-no-cases-empty-state">
                      <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                        <Briefcase className="h-8 w-8 text-amber-400" />
                      </div>
                      <h4 className="font-medium text-secondary mb-2">{t('provider.messaging.newConversation.noCasesTitle')}</h4>
                      <p className="text-sm text-slate-500 mb-4 max-w-xs mx-auto">
                        {t('provider.messaging.newConversation.noCasesBody')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          value={caseSearch}
                          onChange={e => setCaseSearch(e.target.value)}
                          placeholder={t('provider.messaging.newConversation.caseSearchPlaceholder')}
                          className="pl-9"
                          data-testid="v3-case-search"
                        />
                      </div>
                      {filteredCases.map(caseItem => (
                        <button
                          key={caseItem.case_id}
                          onClick={() => handleSelectCase(caseItem.case_id)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border hover:border-primary hover:bg-primary/5 transition-all text-left"
                          data-testid={`v3-case-option-${caseItem.case_id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Briefcase className="h-5 w-5 text-amber-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{caseItem.treatment_name || t('provider.messaging.newConversation.treatmentCaseFallback')}</p>
                            <p className="text-xs text-slate-400 truncate">
                              {t('provider.messaging.newConversation.caseStatusLine', { status: caseItem.status, caseId: caseItem.case_id.slice(0, 8) })}
                            </p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-slate-300" />
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Step 3: Compose Message */}
              {step === 3 && (
                <div className="space-y-4" data-testid="v3-compose-form">
                  <div>
                    <label className="text-sm font-medium text-foreground/80 block mb-1">{t('provider.messaging.newConversation.subjectLabel')}</label>
                    <Input
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      placeholder={t('provider.messaging.newConversation.subjectPlaceholder')}
                      data-testid="v3-thread-subject"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground/80 block mb-1">{t('provider.messaging.newConversation.messageLabel')}</label>
                    <textarea
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder={t('provider.messaging.newConversation.messagePlaceholder')}
                      rows={5}
                      className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      data-testid="v3-thread-message"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {step === 3 && (
          <div className="flex gap-2 justify-end px-5 py-4 border-t">
            <Button variant="outline" onClick={handleClose} data-testid="v3-cancel-btn">
              {t('provider.messaging.newConversation.cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !subject.trim() || !message.trim()}
              data-testid="v3-send-btn"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {creating ? t('provider.messaging.newConversation.sending') : t('provider.messaging.newConversation.startConversation')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewConversationV3Modal;
